import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import { toast } from "sonner";
import { googleAuth, startSession, homePathFor } from "./authV2Services";
import { clearSignup, writeSignup } from "./signupState";
import { routeAfterVerify } from "./signupFlow";
import { SocialButton } from "./pieces";
import { usePhoneTheme } from "../../ForBrands/useIsPhone";

// 16px from 640px up; 18px on phones (Figma "social-btn" G, 2312:4208).
const GoogleGlyph = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.6 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.3l7.8 6C12.3 13.6 17.7 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.6 5.9c4.4-4.1 7-10.1 7-17.6z" />
    <path fill="#FBBC05" d="M10.4 28.7c-.5-1.5-.8-3-.8-4.7s.3-3.2.8-4.7l-7.8-6C.9 16.5 0 20.1 0 24s.9 7.5 2.6 10.7l7.8-6z" />
    <path fill="#34A853" d="M24 48c6.2 0 11.6-2 15.5-5.6l-7.6-5.9c-2.1 1.4-4.8 2.3-7.9 2.3-6.3 0-11.7-4.1-13.6-9.8l-7.8 6C6.5 42.6 14.6 48 24 48z" />
  </svg>
);

export const googleEnabled = () => !!process.env.REACT_APP_GOOGLE_CLIENT_ID;

// Continue with Google, in the same social-btn shape. An existing account
// (any role) signs straight in and gets its Google id linked. A new email
// arrives already verified by Google, so it skips the code step and goes
// on to the name step for the type picked on the welcome screen; with no
// type yet (log in screen) the type screen asks. Typed emails and mobile
// numbers still confirm a code.
const GoogleButton = ({ label = "Google", accountType = null }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [busy, setBusy] = useState(false);
  const phone = usePhoneTheme();

  const finish = async (data) => {
    if (data.newUser) {
      // Only the welcome screen passes a type; the log in screen never does,
      // so a first-time visitor is asked. Anything left in storage from an
      // earlier visit is ignored and cleared.
      clearSignup();
      const next = writeSignup({
        ...(accountType ? { accountType } : {}),
        email: data.email, ticket: data.ticket, firstName: data.firstName || "", lastName: data.lastName || "", phone: "", viaGoogle: true,
      });
      navigate(routeAfterVerify(next));
      return;
    }
    await startSession(data, dispatch);
    toast.success("You're in.");
    if (data.mustChangePassword) { navigate("/change-password?firstLogin=1"); return; }
    navigate(homePathFor(data.permissions || []));
  };

  const login = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setBusy(true);
      try {
        const { ok, data } = await googleAuth({ accessToken: tokenResponse.access_token });
        if (!ok) { toast.error(data?.message || "Google sign-in failed."); return; }
        await finish(data);
      } catch {
        toast.error("We could not reach CACHE. Try again.");
      } finally { setBusy(false); }
    },
    onError: () => toast.error("Google sign-in was cancelled."),
    // The popup failed to open, or closed before Google answered. Say so,
    // instead of leaving the button looking as if nothing happened.
    onNonOAuthError: (err) => {
      setBusy(false);
      if (err?.type === "popup_failed_to_open") {
        toast.error("Google could not open its sign-in window. Allow pop-ups for this site and try again.");
      } else if (err?.type === "popup_closed") {
        toast.error("The Google window closed before sign-in finished. Try again.");
      } else {
        toast.error("Google sign-in did not finish. Try again.");
      }
    },
  });

  return (
    <SocialButton
      tile="border border-bm-border bg-white"
      glyph={<GoogleGlyph size={phone ? 18 : 16} />}
      label={label}
      enabled={googleEnabled()}
      soon
      busy={busy}
      onClick={() => login()}
    />
  );
};

export default GoogleButton;
